import { Box } from "@mui/material";

import { Navigate, Route, Routes, useParams } from "react-router-dom";

import { useFindSubjectByIdSubjectsIdGetQuery } from "api";
import { can } from "authorization/authorization.util";
import { ZonePermissionEnum } from "authorization/types";
import { ErrorPage, NoDataPage } from "components/pages";
import { useAuthenticatedUser } from "hooks/useAuthenticatedUser";

import { TabsMenu } from "./components/TabsMenu";
import { History } from "./History/History";
import { Sessions } from "./Sessions/Sessions";

export function SubjectScreen() {
  const { subjectId = "" } = useParams<{ subjectId: string }>();

  const { permissions } = useAuthenticatedUser();

  const {
    data: subjectData,
    isLoading,
    isError,
    refetch,
  } = useFindSubjectByIdSubjectsIdGetQuery({
    id: subjectId,
    embed: ["devices"],
  });

  const subject = subjectData?.data;
  const enableSleepHistory = can(
    permissions,
    ZonePermissionEnum["zone.sleep_history"],
  );

  if (isLoading) {
    return (
      <Box>
        <TabsMenu
          subjectId={subjectId}
          subject={null}
          showSleepHistory={enableSleepHistory}
        />
        <Box sx={{ paddingInline: 5, paddingBlock: 3 }}>
          <Routes>
            <Route index element={<Navigate to={"sessions"} />} />
            <Route
              path={"sessions"}
              element={<Sessions subjectId={subjectId} />}
            />
            {enableSleepHistory && (
              <Route
                path={"history"}
                element={<History subjectId={subjectId} />}
              />
            )}
          </Routes>
        </Box>
      </Box>
    );
  }

  if (isError) {
    return (
      <ErrorPage
        title={"Failed to load subject"}
        buttonText={"Try again"}
        buttonOnClick={refetch}
      />
    );
  }

  if (!subject) {
    return <NoDataPage title={"Subject not found"} />;
  }

  return (
    <Box
      sx={(theme) => ({
        backgroundColor: theme.vars.palette.backgrounds.neutral.cold.default,
        minHeight: "calc(100vh - 54px)",
        "@media print": {
          width: "100%",
        },
      })}
      id={"print-area"}
    >
      <TabsMenu
        subjectId={subjectId}
        subject={subject}
        showSleepHistory={enableSleepHistory}
      />
      <Box sx={{ paddingInline: [1.5, 1.5, 5], paddingBlock: [2, 2, 3] }}>
        <Routes>
          <Route index element={<Navigate to={"sessions"} />} />
          <Route
            path={"sessions"}
            element={<Sessions subjectId={subjectId} />}
          />
          {enableSleepHistory && (
            <Route
              path={"history"}
              element={<History subjectId={subjectId} />}
            />
          )}
        </Routes>
      </Box>
    </Box>
  );
}
